/**
 * Secure secret storage abstraction.
 *
 * Desktop (Tauri): uses OS keychain via Rust `keyring` crate
 *   (Windows Credential Manager, macOS Keychain, Linux Secret Service).
 *   Secrets never touch the filesystem in plaintext.
 *
 * Web: uses AES-GCM encryption (Web Crypto API) around localStorage.
 *   The encryption key is stored in IndexedDB (separate origin store),
 *   so raw localStorage reads return ciphertext only.
 *
 * API keys, tokens, and other secrets go through this interface.
 */

import { isTauriRuntime } from '@/utils/tauri';

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface SecretStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  delete(key: string): Promise<void>;
  getAll(): Promise<Record<string, string>>;
}

/** Standard key names for all secrets. */
export const SECRET_KEYS = {
  // AI providers
  ANTHROPIC_API_KEY: 'anthropic-api-key',
  // Workspace id, not a secret — stored alongside the key because
  // identity-linked keys are unusable without it (Anthropic returns 400).
  ANTHROPIC_WORKSPACE_ID: 'anthropic-workspace-id',
  OPENROUTER_API_KEY: 'openrouter-api-key',
  // Data sources
  ACLED_EMAIL: 'acled-email',
  ACLED_PASSWORD: 'acled-password',
  VESSELFINDER_API_KEY: 'vesselfinder-api-key',
  OPENSKY_USERNAME: 'opensky-username',
  OPENSKY_PASSWORD: 'opensky-password',
  GDACS_API_KEY: 'gdacs-api-key',
  FIRMS_MAP_KEY: 'firms-map-key',
  // Infrastructure
  MAPTILER_API_KEY: 'maptiler-api-key',
  UPSTASH_REDIS_URL: 'upstash-redis-url',
  UPSTASH_REDIS_TOKEN: 'upstash-redis-token',
  // OLLAMA_ENDPOINT is in frontend SECRET_KEYS but intentionally omitted from
  // the Rust SECRET_KEY_MAPPINGS — the browser-side Ollama provider connects
  // directly to the local Ollama instance; the sidecar never proxies Ollama.
  OLLAMA_ENDPOINT: 'ollama-endpoint',
} as const;

export type SecretKeyName = (typeof SECRET_KEYS)[keyof typeof SECRET_KEYS];

// ---------------------------------------------------------------------------
// Tauri implementation (OS keychain via Rust keyring crate)
// ---------------------------------------------------------------------------

class TauriKeychainStore implements SecretStore {
  private invoke: ((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null = null;
  private loading: Promise<void> | null = null;

  private async ensureInvoke(): Promise<void> {
    if (this.invoke) return;
    if (!this.loading) {
      this.loading = (async () => {
        const mod = await import(/* @vite-ignore */ '@tauri-apps/api/core');
        this.invoke = mod.invoke;
      })();
    }
    await this.loading;
  }

  async get(key: string): Promise<string | null> {
    await this.ensureInvoke();
    const val = await this.invoke!('keychain_get', { key }) as string | null;
    return val ?? null;
  }

  async set(key: string, value: string): Promise<void> {
    await this.ensureInvoke();
    await this.invoke!('keychain_set', { key, value });
  }

  async delete(key: string): Promise<void> {
    await this.ensureInvoke();
    await this.invoke!('keychain_delete', { key });
  }

  async getAll(): Promise<Record<string, string>> {
    const result: Record<string, string> = {};
    for (const k of Object.values(SECRET_KEYS)) {
      const val = await this.get(k);
      if (val) result[k] = val;
    }
    return result;
  }
}

// ---------------------------------------------------------------------------
// Web implementation (AES-GCM encrypted localStorage)
// ---------------------------------------------------------------------------

const LS_PREFIX = 'sentinel-secret:';
const IDB_KEY_STORE = 'sentinel-keystore';
const IDB_KEY_NAME = 'encryption-key';

/**
 * Encrypted localStorage store using Web Crypto API.
 * Generates an AES-GCM-256 key on first use and stores it in IndexedDB.
 * Secrets in localStorage are IV + ciphertext, base64-encoded.
 */
class EncryptedLocalStorageStore implements SecretStore {
  private cryptoKey: CryptoKey | null = null;
  private keyLoading: Promise<CryptoKey> | null = null;

  private async getKey(): Promise<CryptoKey> {
    if (this.cryptoKey) return this.cryptoKey;
    if (this.keyLoading) return this.keyLoading;

    this.keyLoading = (async () => {
      // Try loading existing key from IndexedDB
      const stored = await this.loadKeyFromIDB();
      if (stored) {
        this.cryptoKey = await crypto.subtle.importKey(
          'raw', stored.buffer as ArrayBuffer, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'],
        );
        return this.cryptoKey;
      }

      // Generate new key
      this.cryptoKey = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'],
      );

      // Export and persist in IndexedDB
      const raw = await crypto.subtle.exportKey('raw', this.cryptoKey);
      await this.saveKeyToIDB(new Uint8Array(raw));

      return this.cryptoKey;
    })();

    return this.keyLoading;
  }

  private openIDB(): Promise<IDBDatabase | null> {
    return new Promise((resolve) => {
      try {
        const req = indexedDB.open(IDB_KEY_STORE, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains('keys')) {
            db.createObjectStore('keys');
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(null);
      } catch { resolve(null); }
    });
  }

  private async loadKeyFromIDB(): Promise<Uint8Array | null> {
    const db = await this.openIDB();
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('keys', 'readonly');
        const store = tx.objectStore('keys');
        const req = store.get(IDB_KEY_NAME);
        req.onsuccess = () => {
          const val = req.result;
          resolve(val instanceof Uint8Array ? val : null);
        };
        req.onerror = () => resolve(null);
      } catch { resolve(null); }
    });
  }

  private async saveKeyToIDB(key: Uint8Array): Promise<void> {
    const db = await this.openIDB();
    if (!db) return;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction('keys', 'readwrite');
        tx.objectStore('keys').put(key, IDB_KEY_NAME);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch { resolve(); }
    });
  }

  private async encrypt(plaintext: string): Promise<string> {
    const key = await this.getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plaintext);
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv }, key, encoded,
    );
    // Prepend IV to ciphertext, encode as base64
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);
    return btoa(String.fromCharCode(...combined));
  }

  private async decrypt(stored: string): Promise<string | null> {
    try {
      const key = await this.getKey();
      const combined = Uint8Array.from(atob(stored), c => c.charCodeAt(0));
      const iv = combined.slice(0, 12);
      const ciphertext = combined.slice(12);
      const plaintext = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv }, key, ciphertext,
      );
      return new TextDecoder().decode(plaintext);
    } catch {
      return null; // Decryption failed (corrupted or wrong key)
    }
  }

  async get(key: string): Promise<string | null> {
    const stored = localStorage.getItem(LS_PREFIX + key);
    if (!stored) return null;
    return this.decrypt(stored);
  }

  async set(key: string, value: string): Promise<void> {
    const encrypted = await this.encrypt(value);
    localStorage.setItem(LS_PREFIX + key, encrypted);
  }

  async delete(key: string): Promise<void> {
    localStorage.removeItem(LS_PREFIX + key);
  }

  async getAll(): Promise<Record<string, string>> {
    const result: Record<string, string> = {};
    for (const k of Object.values(SECRET_KEYS)) {
      const val = await this.get(k);
      if (val) result[k] = val;
    }
    return result;
  }
}

// ---------------------------------------------------------------------------
// Plain fallback (non-HTTPS environments without Web Crypto)
// ---------------------------------------------------------------------------

class PlainLocalStorageStore implements SecretStore {
  async get(key: string): Promise<string | null> {
    return localStorage.getItem(LS_PREFIX + key);
  }

  async set(key: string, value: string): Promise<void> {
    localStorage.setItem(LS_PREFIX + key, value);
  }

  async delete(key: string): Promise<void> {
    localStorage.removeItem(LS_PREFIX + key);
  }

  async getAll(): Promise<Record<string, string>> {
    const result: Record<string, string> = {};
    for (const k of Object.values(SECRET_KEYS)) {
      const val = localStorage.getItem(LS_PREFIX + k);
      if (val) result[k] = val;
    }
    return result;
  }
}

// ---------------------------------------------------------------------------
// Factory + singleton
// ---------------------------------------------------------------------------

function createSecretStore(): SecretStore {
  if (isTauriRuntime()) {
    return new TauriKeychainStore();
  }
  // Use encrypted storage when Web Crypto API is available (HTTPS or localhost)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    return new EncryptedLocalStorageStore();
  }
  return new PlainLocalStorageStore();
}

export const secretStore = createSecretStore();

// ---------------------------------------------------------------------------
// Migration — move old plaintext secrets to new secure stores
// ---------------------------------------------------------------------------

const LEGACY_STORAGE_KEY = 'sentinel-ai-settings';
const MIGRATED_FLAG = 'sentinel-secrets-migrated-v2';

/**
 * On first launch, migrate API keys from localStorage
 * (old sentinel-ai-settings blob + old plaintext sentinel-secret: keys)
 * into the secure keychain/encrypted store. Returns true if migration occurred.
 */
export async function migrateSecretsFromLocalStorage(): Promise<boolean> {
  // Skip if already migrated to v2 format
  if (localStorage.getItem(MIGRATED_FLAG)) return false;

  try {
    let migrated = false;

    // --- Migrate from old AI settings blob ---
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (raw) {
      const settings = JSON.parse(raw) as Record<string, Record<string, string>>;

      if (settings.anthropic?.apiKey) {
        await secretStore.set(SECRET_KEYS.ANTHROPIC_API_KEY, settings.anthropic.apiKey);
        settings.anthropic.apiKey = '';
        migrated = true;
      }

      if (settings.openrouter?.apiKey) {
        await secretStore.set(SECRET_KEYS.OPENROUTER_API_KEY, settings.openrouter.apiKey);
        settings.openrouter.apiKey = '';
        migrated = true;
      }

      if (migrated) {
        localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(settings));
      }
    }

    // --- Migrate old plaintext sentinel-secret: keys ---
    // Re-store through the new encrypted/keychain backend
    for (const k of Object.values(SECRET_KEYS)) {
      const lsKey = LS_PREFIX + k;
      const plainVal = localStorage.getItem(lsKey);
      if (!plainVal) continue;

      // Heuristic: if it's already base64 ciphertext (from EncryptedLocalStorageStore),
      // skip. Old plaintext keys are human-readable (start with letters, contain dashes).
      const looksEncrypted = /^[A-Za-z0-9+/=]{24,}$/.test(plainVal) && plainVal.length > 30;
      if (!looksEncrypted) {
        await secretStore.set(k, plainVal);
        // Only remove the old key on desktop (where keychain is separate)
        if (isTauriRuntime()) {
          localStorage.removeItem(lsKey);
        }
        migrated = true;
      }
    }

    localStorage.setItem(MIGRATED_FLAG, '1');
    return migrated;
  } catch {
    // Don't block boot on migration failure
    return false;
  }
}
